/**
 * Referent API Endpoints
 * Centralizes all referent-related API calls. `id` = documentId.
 */
import type { AxiosResponse } from "axios";
import api from "../api";
import type { ApiListResponse, ApiResponse, EntityPayload, QueryParams } from "../../types/api";
import type { Referent, VbSemRef } from "../../types/entities";

export const referentApi = {
  getAll: (params: QueryParams = {}): Promise<ApiListResponse<Referent>> =>
    api.get("/api/referents", { params }),

  getById: (id: string, params: QueryParams = {}): Promise<ApiResponse<Referent>> =>
    api.get(`/api/referents/${id}`, { params }),

  create: (data: EntityPayload): Promise<ApiResponse<Referent>> =>
    api.post("/api/referents", { data }),

  update: (id: string, data: EntityPayload): Promise<ApiResponse<Referent>> =>
    api.put(`/api/referents/${id}`, { data }),

  delete: (id: string): Promise<AxiosResponse> => api.delete(`/api/referents/${id}`),

  /**
   * Get referents by seminar (via the vb-sem-ref junction). The junction row
   * carries a numeric `id_seminar`, so this filter uses the numeric seminar id.
   * Accepts `undefined` too: seminarWp's absent-relation path calls this with a
   * possibly-undefined id (the filter value is simply omitted by axios then).
   */
  getBySeminar: (seminarId: string | number | undefined): Promise<ApiListResponse<VbSemRef>> =>
    api.get("/api/vb-sem-refs", {
      params: {
        "filters[id_seminar]": seminarId,
        populate: "*",
      },
    }),

  /** Create a seminar↔referent relationship (vb-sem-ref junction row). */
  createSeminarReferent: (data: EntityPayload): Promise<ApiResponse<VbSemRef>> =>
    api.post("/api/vb-sem-refs", { data }),

  /**
   * Update a seminar↔referent relationship by junction documentId.
   *
   * This is where the PER-SEMINAR honorar lives: `ref_brutto_honorar_euro` and
   * `ist_mehrwert_steuer` are columns on the junction, copied from the referent's
   * profile when the row is created, so editing them here changes the fee for this
   * seminar only and leaves the referent's own default untouched.
   */
  updateSeminarReferent: (id: string, data: EntityPayload): Promise<ApiResponse<VbSemRef>> =>
    api.put(`/api/vb-sem-refs/${id}`, { data }),

  /** Delete a seminar↔referent relationship by junction documentId. */
  deleteSeminarReferent: (id: string): Promise<AxiosResponse> =>
    api.delete(`/api/vb-sem-refs/${id}`),
};
